import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  FormHelperText,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Snackbar,
  Box,
  Button,
  Typography,
  Alert,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useDispatch } from "react-redux";
import {
  getTokenID,
  isEmployerLogin,
  permissionStatus,
  role,
} from "../redux/modules/userSlice";
import Cookies from "js-cookie";
import ModalLoadingOverlay, {
  modalLoadingContainerSx,
} from "../components/ModalLoadingOverlay";

const defaultTheme = createTheme();

const Changepassword = ({ open: openProp, onClose }) => {
  const nav = useNavigate();
  const dispatch = useDispatch();

  // ── Agar open prop nahi diya toh internal state se manage karo ──
  const [internalOpen, setInternalOpen] = useState(true);
  const isOpen = openProp !== undefined ? openProp : internalOpen;

  const [currentPassword, setCurrentPassword] = useState("");
  const [currentPasswordError, setCurrentPasswordError] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);

  const [password, setPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [oldPassworderror, setoldPassworderror] = useState("");
  const [loading, setLoading] = useState(false);

  const combinedCurrentPasswordError = currentPasswordError || oldPassworderror;

  const handleClose = () => {
    setCurrentPassword("");
    setPassword("");
    setConfirmPassword("");
    setCurrentPasswordError("");
    setPasswordError("");
    setConfirmPasswordError("");
    setoldPassworderror("");
    setInternalOpen(false);
    if (onClose) {
      onClose();
    } else {
      nav(-1);
    }
  };

  const handleSnackbarOpen = (message) => {
    setSnackbarMessage(message);
    setOpenSnackbar(true);
  };

  const handleSnackbarClose = () => setOpenSnackbar(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setPasswordError("");
    setConfirmPasswordError("");
    setCurrentPasswordError("");
    setoldPassworderror("");

    if (password.length < 6) {
      setPasswordError("Password must be more than 5 letters.");
      return;
    }
    if (password === currentPassword) {
      setPasswordError(
        "New password must not be the same as the current password.",
      );
      return;
    }
    if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match!");
      return;
    }

    setLoading(true);
    axios
      .post(
        `change-user-password`,
        { newPassword: confirmPassword, oldPassword: currentPassword },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      )
      .then((response) => {
        if (response.data.status === 200) {
          handleSnackbarOpen("Password Changed Successfully");
          setTimeout(() => {
            dispatch(getTokenID(null));
            dispatch(permissionStatus(null));
            dispatch(isEmployerLogin(false));
            dispatch(role(null));
            const d = new Date();
            let expires = "expires=" + d.toUTCString();
            document.cookie =
              "_secure_ARJ_" + "=" + "" + ";" + expires + ";path=/";
            document.cookie =
              "_secure_ref" + "=" + "" + ";" + expires + ";path=/";
            document.cookie =
              "cookiesAccepted" + "=" + "" + ";" + expires + ";path=/";
            localStorage.clear();
            window.location.href = "/login";
          }, 1500);
        } else {
          handleSnackbarOpen(response.data.error);
        }
      })
      .catch((err) => {
        console.error(err);
        handleSnackbarOpen(
          err.response?.data?.error || "Something went wrong.",
        );
        setoldPassworderror(
          err.response?.data?.error || "Incorrect current password.",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const isSaveDisabled = !currentPassword || !password || !confirmPassword;

  // Shared OutlinedInput sx
  const getInputSx = (hasError) => ({
    borderRadius: "8px",
    fontSize: "14px",
    backgroundColor: "#fff",
    "& fieldset": { borderColor: hasError ? "#EF4444" : "#E2E8F0" },
    "&:hover fieldset": { borderColor: "#3F95EA" },
    "&.Mui-focused fieldset": { borderColor: "#3F95EA", borderWidth: "1px" },
  });

  return (
    <ThemeProvider theme={defaultTheme}>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={
            snackbarMessage === "Password Changed Successfully"
              ? "success"
              : "error"
          }
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: modalLoadingContainerSx(loading, {
            borderRadius: "16px",
            boxShadow: "0px 8px 32px rgba(0,0,0,0.15)",
            overflow: "hidden",
          }),
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            px: "20px",
            py: "14px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{ fontSize: "16px", fontWeight: 600, color: "#0F172A" }}
          >
            Change Password
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0.5 }}>
            <CloseIcon sx={{ fontSize: "20px", color: "#6B7280" }} />
          </IconButton>
        </Box>

        {/* ── Warning banner ── */}
        <Box
          sx={{
            mx: "20px",
            mb: "4px",
            px: "12px",
            py: "10px",
            backgroundColor: "#FFF8E1",
            border: "1px solid #FDE68A",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: "13px", color: "#92400E" }}>
            ⓘ You will be logged out of all active sessions.
          </Typography>
        </Box>

        {/* ── Body ── */}
        <DialogContent sx={{ px: "20px", pt: "16px", pb: "4px" }}>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            {/* Current Password */}
            <Box>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#374151",
                  mb: 0.8,
                }}
              >
                Current Password <span style={{ color: "#EF4444" }}>*</span>
              </Typography>
              <OutlinedInput
                fullWidth
                size="small"
                placeholder="Enter Current Password"
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                error={!!combinedCurrentPasswordError}
                onFocus={() => {
                  setCurrentPasswordError("");
                  setoldPassworderror("");
                }}
                onChange={(e) => setCurrentPassword(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword((s) => !s)}
                      edge="end"
                      sx={{ color: "#9CA3AF" }}
                    >
                      {showCurrentPassword ? (
                        <VisibilityOff sx={{ fontSize: 18 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                sx={getInputSx(!!combinedCurrentPasswordError)}
              />
              {combinedCurrentPasswordError && (
                <FormHelperText error sx={{ ml: 0, mt: 0.5, fontSize: "12px" }}>
                  {combinedCurrentPasswordError}
                </FormHelperText>
              )}
            </Box>

            {/* New Password */}
            <Box>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#374151",
                  mb: 0.8,
                }}
              >
                New Password <span style={{ color: "#EF4444" }}>*</span>
              </Typography>
              <OutlinedInput
                fullWidth
                size="small"
                placeholder="Enter New Password"
                type={showNewPassword ? "text" : "password"}
                value={password}
                error={!!passwordError}
                onChange={(e) => setPassword(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword((s) => !s)}
                      edge="end"
                      sx={{ color: "#9CA3AF" }}
                    >
                      {showNewPassword ? (
                        <VisibilityOff sx={{ fontSize: 18 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                sx={getInputSx(!!passwordError)}
              />
              {passwordError && (
                <FormHelperText error sx={{ ml: 0, mt: 0.5, fontSize: "12px" }}>
                  {passwordError}
                </FormHelperText>
              )}
            </Box>

            {/* Confirm Password */}
            <Box>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#374151",
                  mb: 0.8,
                }}
              >
                Confirm Password <span style={{ color: "#EF4444" }}>*</span>
              </Typography>
              <OutlinedInput
                fullWidth
                size="small"
                placeholder="Re-Enter New Password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                error={!!confirmPasswordError}
                onChange={(e) => setConfirmPassword(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword((s) => !s)}
                      edge="end"
                      sx={{ color: "#9CA3AF" }}
                    >
                      {showConfirmPassword ? (
                        <VisibilityOff sx={{ fontSize: 18 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                sx={getInputSx(!!confirmPasswordError)}
              />
              {confirmPasswordError && (
                <FormHelperText error sx={{ ml: 0, mt: 0.5, fontSize: "12px" }}>
                  {confirmPasswordError}
                </FormHelperText>
              )}
            </Box>

            {/* Forgot Password */}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Typography
                component="a"
                href="/forgetpassword"
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#3F95EA",
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Forgot Password?
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        {/* ── Footer ── */}
        <DialogActions
          sx={{ px: "20px", py: "14px", gap: 1.5, justifyContent: "flex-end" }}
        >
          <Button
            className="cancel-btn"
            variant="outlined"
            onClick={handleClose}
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: "#6B7280",
              borderColor: "#D1D5DB",
              borderRadius: "8px",
              px: 2.5,
              py: 0.8,
              boxShadow: "none",
              "&:hover": {
                borderColor: "#9CA3AF",
                backgroundColor: "#F9FAFB",
                boxShadow: "none",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSaveDisabled}
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              borderRadius: "8px",
              px: 2.5,
              py: 0.8,
              backgroundColor: "#3F95EA",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#006CB7", boxShadow: "none" },
              "&.Mui-disabled": {
                backgroundColor: "#E5E7EB",
                color: "#9CA3AF",
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
        <ModalLoadingOverlay open={loading} />
      </Dialog>
    </ThemeProvider>
  );
};

export default Changepassword;
